import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

const SplashScreen = ({ onComplete }) => {
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Check if splash has been shown in this session
    const splashShown = sessionStorage.getItem('splashShown');
    
    if (splashShown) {
      // Skip splash if already shown
      onComplete();
      return;
    }
  }, [onComplete]); // Add onComplete to deps

  const startExperience = () => {
    setHasStarted(true);

    // Audio removed per user request - keeping visual countdown only
    setIsPlaying(true);

    // Countdown timer (visual effect)
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Fade out and complete after countdown duration
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 9500); // Start fade 9.5 seconds in

    const completeTimer = setTimeout(() => {
      sessionStorage.setItem('splashShown', 'true');
      onComplete();
    }, 11000); // Complete at 11 seconds
  };

  return (
    <div 
      className={`fixed inset-0 z-[9999] bg-black flex items-center justify-center transition-opacity duration-1000 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="text-center relative">
        {/* Animated background elements */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-96 h-96 bg-light-pink/10 rounded-full blur-3xl animate-pulse" />
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-64 h-64 bg-mid-purple/10 rounded-full blur-3xl animate-pulse animation-delay-500" />
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-8">
          {/* Logo/Icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <Sparkles className="w-32 h-32 text-light-pink animate-spin-slow" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-6xl font-display font-bold text-white">
                  {countdown > 0 ? countdown : '🚀'}
                </div>
              </div>
            </div>
          </div>

          {/* Studio Name */}
          <div className="space-y-4">
            <h1 className="text-6xl lg:text-8xl font-display font-bold text-white animate-pulse">
              INSPIRE
            </h1>
            <h2 className="text-4xl lg:text-6xl font-display font-bold text-light-pink animate-pulse animation-delay-300">
              STUDIO
            </h2>
            <h3 className="text-3xl lg:text-5xl font-display font-bold text-mid-yellow animate-pulse animation-delay-600">
              OXFORD
            </h3>
          </div>

          {/* Tagline */}
          {!hasStarted ? (
            <>
              <p className="text-xl text-mid-grey mb-8">
                Welcome to the magic...
              </p>
              <button
                onClick={startExperience}
                className="bg-light-pink text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-mid-yellow transition-all duration-300 transform hover:scale-105 animate-pulse"
              >
                🚀 Click to Enter
              </button>
            </>
          ) : (
            <>
              <p className="text-xl text-mid-grey animate-pulse animation-delay-1000">
                Preparing for launch...
              </p>

              {/* Click to skip (appears after 2 seconds) */}
              {countdown < 9 && (
                <button
                  onClick={() => {
                    setFadeOut(true);
                    setTimeout(() => {
                      sessionStorage.setItem('splashShown', 'true');
                      onComplete();
                    }, 500);
                  }}
                  className="text-sm text-mid-grey hover:text-white transition-colors mt-8 animate-fade-in"
                >
                  Click to skip
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animation-delay-300 {
          animation-delay: 0.3s;
        }

        .animation-delay-500 {
          animation-delay: 0.5s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        .animation-delay-1000 {
          animation-delay: 1s;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-in forwards;
        }
      `}</style>
    </div>
  );
};

export default SplashScreen;
